import { Tab } from '../Tab/types'

export class Rect {
  x: number
  y: number
  width: number
  height: number

  constructor(x: number, y: number, width: number, height: number) {
    this.x = x
    this.y = y
    this.width = width
    this.height = height
  }

  midX(): number {
    return this.x + this.width / 2
  }

  midY(): number {
    return this.y + this.height / 2
  }

  maxX(): number {
    return this.x + this.width
  }

  maxY(): number {
    return this.y + this.height
  }
}

export class CapsuleProps {
  rect: Rect
  radius: number
  color: string | CanvasGradient
  borderColor?: string
  borderWidth: number

  constructor(rect: Rect, radius: number, color: string | CanvasGradient, borderColor?: string, borderWidth: number = 1) {
    this.rect = rect
    this.radius = radius
    this.color = color
    this.borderColor = borderColor
    this.borderWidth = borderWidth
  }
}

export enum VenueStatusColor {
  electric_purple_1 = '#BF5AF2',
  electric_purple_2 = '#9237E2',
  vip_yellow = '#F7E224',
  border_gray = '#333333',
  background_gray = '#1B1B1B',
  background_gray_2 = '#333333',
  blue_violet = '#2D2CF4',
  blue_1 = '#4140ED',
  chartreuse = '#BCDC06',
  berry_red = '#FB0000',
  blue_4 = '#1A198C',
}

export interface VenueCustomer {
  customer_id: number
  venues: VenueCustomerStatus[]
}

export type VenueStatusTabInformation = {
  tab_uuid: string
  venue_uuid: string
  venue_status_points_earned: number | null | undefined
  venue_status_signup_bonus_points: number | null | undefined
  venue_status_points_redeemed: number | null | undefined
  venue_status_amount_redeemed: string | null | undefined
  vip: boolean | null | undefined
}

export type VenueCustomerStatus = {
  venue_uuid: string
  venue_status_text_opt_in?: boolean
  vip: boolean
  venue_status_points_balance: number
  venue_status_redeemable_amount: string
  venue_status_redeemable_points: number
  venue_status_claimable_points: number
  venue_status_claimable_amount: string
  venue_name: string
  venue_status_vip_points_multiplier: number
  venue_status_points_threshold_for_reward: number
  venue_status_points_to_next_reward: number
  venue_status_points_toward_next_reward: number
  venue_status_reward_dollar_amount: string
  venue_status_signup_on?: string
  venue_status_signup_bonus_points?: number
}

export type VenueStatusClaim = {
  customer_id: number
  venue_uuid: string
  venue_name: string
  venue_status_text_opt_in?: boolean
  venue_status_signup_on?: string
  venue_status_points_balance: number
  venue_status_redeemable_points: number
  venue_status_redeemable_amount: string
  venue_status_claimable_points: number
  venue_status_claimable_amount: string
  venue_status_points_to_next_reward: number
  venue_status_points_toward_next_reward: number
  venue_status_vip_points_multiplier: number
  venue_status_points_threshold_for_reward: number
  venue_status_reward_dollar_amount: string
  vip: boolean
  tab: VenueStatusClaimTab
}

export type VenueStatusClaimTab = {
  tab_uuid: string
  venue_status_points_earned: number | null | undefined
  venue_status_signup_bonus_points: number | null | undefined
  venue_status_points_redeemed: number | null | undefined
  venue_status_amount_redeemed: string | null | undefined
}

export interface VenueStatusHistoryTransactionApi {
  created_on: string
  description: string
  points_change: number
  vip_status: boolean
}

export interface VenueStatusHistory {
  transactions: Array<VenueStatusHistoryTransactionApi>
}

export class VenueStatusHistoryTransaction {
  description: string
  color: string
  points_change: string
  points: number
  created: Date
  createdDate: string
  createdMonth: string

  constructor(description: string, points_change: number, vip_status: boolean, created_on: string) {
    this.description = description
    this.color = vip_status ? VenueStatusColor.vip_yellow : VenueStatusColor.electric_purple_1
    this.points_change = `${points_change > 0 ? '+' : ''}${points_change}`
    this.points = points_change
    this.created = new Date(created_on)
    this.createdDate = this.created.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    this.createdMonth = this.created.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
  }
}

export type VenueStatusSubmit = {
  venue_uuid: string
  venue_status_text_opt_in: boolean | null
}

export type VenueStatusClaimSubmit = {
  venue_uuid: string
  tab_uuid: string
}

export type VenueStatusData = {
  venue_uuid: string
  points: number | null | undefined
  previousPoints?: number | null | undefined
  threshold: number | undefined
  nextPoints: number | null | undefined
  title: string | undefined
  rewardDollarAmount: string | null | undefined
  redeemedOrEarnedAmount?: string | null | undefined
  availableAmount?: string | null | undefined
  vip: boolean | undefined
  vipMultiplier: number | undefined
  werePointsRedeemed: boolean | undefined
  werePointsEarned?: boolean | undefined
  firstTimeBonusPoints: number | undefined
}

export enum BalloonAnimationState {
  presentationAllowed,
  tabScreenNotPresent,
  orderInstructions,
  postOrderAds,
  payment,
  balloonAnimation,
}

export const processData = (
  tab?: Tab | null,
  venueStatus?: VenueCustomerStatus | null,
  previousVenueCustomerStatus?: VenueCustomerStatus | null,
  venueStatusClaim?: VenueStatusClaim | null,
  venueStatusSignupBonusPoints?: number | null | undefined
): VenueStatusData => {
  const firstTimeBonusPoints =
    venueStatusSignupBonusPoints ??
    venueStatusClaim?.tab?.venue_status_signup_bonus_points ??
    tab?.venue_status_signup_bonus_points ??
    0
  const currentPoints = venueStatus?.venue_status_points_toward_next_reward ?? 0
  const previousPoints =
    currentPoints === 0 || currentPoints < (previousVenueCustomerStatus?.venue_status_points_toward_next_reward ?? 0)
      ? 0
      : previousVenueCustomerStatus?.venue_status_points_toward_next_reward ?? 0

  const werePointsRedeemed =
    parseFloat(venueStatusClaim?.tab?.venue_status_amount_redeemed ?? tab?.venue_status_amount_redeemed ?? '0.00') > 0
  const werePointsEarned = parseFloat(venueStatus?.venue_status_claimable_amount ?? '0.00') > 0
  const redeemedOrEarnedAmount =
    (werePointsRedeemed
      ? venueStatusClaim?.tab?.venue_status_amount_redeemed ?? tab?.venue_status_amount_redeemed
      : werePointsEarned
      ? venueStatus?.venue_status_claimable_amount
      : '0.00') ?? '0.00'

  return {
    venue_uuid: venueStatus?.venue_uuid ?? '',
    points: currentPoints,
    previousPoints: previousPoints,
    threshold: venueStatus?.venue_status_points_threshold_for_reward,
    nextPoints: venueStatus?.venue_status_points_to_next_reward,
    title: venueStatus?.venue_name,
    rewardDollarAmount: venueStatus?.venue_status_reward_dollar_amount,
    redeemedOrEarnedAmount: redeemedOrEarnedAmount,
    availableAmount: venueStatus?.venue_status_redeemable_amount,
    vip: venueStatus?.vip,
    vipMultiplier: venueStatus?.venue_status_vip_points_multiplier,
    werePointsRedeemed: werePointsRedeemed,
    werePointsEarned: werePointsEarned,
    firstTimeBonusPoints: firstTimeBonusPoints,
  }
}

export function getRandomIntInclusive(min: number, max: number): number {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min + 1) + min)
}

export function getRandomFloatInclusive(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

export function getRandomArrayElement<T>(arr: T[]): T | undefined {
  if (arr.length === 0) {
    return undefined
  }
  const randomIndex: number = Math.floor(Math.random() * arr.length)
  return arr[randomIndex]
}

const shouldCacheVenueCustomer = (oldVenueCustomer: VenueCustomer | null, newVenueCustomer: VenueCustomer | null) => {
  if (!newVenueCustomer?.venues?.length || !oldVenueCustomer?.venues?.length) {
    return false
  }
  const newVenueStatusPoints = newVenueCustomer?.venues[0].venue_status_points_toward_next_reward ?? 0
  const previousVenueStatusThreshold = oldVenueCustomer?.venues[0].venue_status_points_threshold_for_reward ?? 100
  const previousVenueStatusPoints = oldVenueCustomer?.venues[0].venue_status_points_toward_next_reward ?? 0

  const newClaimablePoints = newVenueCustomer?.venues[0].venue_status_claimable_points ?? 0
  const previousClaimablePoints = oldVenueCustomer?.venues[0].venue_status_claimable_points ?? 0

  return (
    (newVenueStatusPoints > previousVenueStatusPoints && newVenueStatusPoints > 0) ||
    previousVenueStatusPoints >= previousVenueStatusThreshold ||
    (newClaimablePoints > previousClaimablePoints && newClaimablePoints > 0)
  )
}

export const cacheVenueCustomer = (
  venueCustomer: VenueCustomer | null,
  venueCustomers: VenueCustomer[],
  setVenueCustomers: (venueCustomers: VenueCustomer[]) => void
) => {
  if (venueCustomer && venueCustomer.venues.length > 0) {
    const venueCustomerIndex = venueCustomers.findIndex(
      (vc) => vc.venues.length > 0 && vc.venues[0].venue_uuid === venueCustomer.venues[0].venue_uuid
    )
    if (venueCustomerIndex !== -1) {
      if (shouldCacheVenueCustomer(venueCustomers[venueCustomerIndex], venueCustomer)) {
        const updatedVenueCustomers = [...venueCustomers]
        updatedVenueCustomers[venueCustomerIndex] = {
          customer_id: venueCustomer.customer_id,
          venues: [venueCustomer.venues[0]],
        }
        setVenueCustomers(updatedVenueCustomers)
      }
    } else {
      setVenueCustomers([...venueCustomers, venueCustomer])
    }
  }
}
